/*!
  Pioneer WFRIA ActivityMonitorUtilities v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ActivityMonitorUtilities"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ActivityMonitorUtilities") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ActivityMonitorUtilities");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

const WRIA_COOKIE_NAME = "WRIA_JAR";
/**
 * ActivityMonitor tracks the activity across multiple windows. If any activity
 * occurs on the window then will write to the WRIA CEO/APP specific cookie and also
 * reading from it if any activity has occurred on any other open windows.
 *
 * WRIA specific cookie name is called `WRIA_JAR`.
 *
 * Below is how the structure of WRIA_JAR cookie looks like:
 * For CEO apps:
 *      WRIA_JAR=ceo:{"u":"","t":"","k":""}
 *
 * For Non-CEO apps:
 *      WRIA_JAR=app:{"u":"","t":"","k":""}
 *
 * For CEO apps, it is preferred to read/write to the `ceo` key of the WRIA_JAR cookie.
 * For Non-CEO apps, it is preferred to read/write to the `app` key of the WRIA_JAR cookie.
 *
 */

const getWriaJarCookieRegex = cookiePrefix => {
  /**
   * The below regular expression is to get the WRIA_JAR cookie containing the `cookiePrefix`.
   */
  return new RegExp(`WRIA_JAR=${cookiePrefix}:([^;]*)(;|$)`);
};
/**
 * Gets the WRIA APP specific Cookie data.
 * @param {String} key Key to get from WRIA_JAR `cookiePrefix`.
 * @param {String} cookiePrefix cookiePrefix that exists in WRIA_JAR cookie.
 * Applicable keys are `app` and `ceo`.
 */


function getCookieData(cookiePrefix, key) {
  /**
   * Get the WRIA APP specific cookie data.
   * document.cookie.match(WRIA_JAR_COOKIE_REGEX) returns an array that contains the
   * entire matched string as the first element followed by the results captured in the
   * parentheses of the regular expresssion passed to the `match` method.
   */
  const wriaJarCookie = document.cookie.match(getWriaJarCookieRegex(cookiePrefix));
  /**
   * Only set the values stored in the `app`/`ceo` key of the WRIA_JAR cookie in the data
   * based on the argument `cookiePrefix`.
   */

  let data = wriaJarCookie && wriaJarCookie[1];

  if (data) {
    /** Converting WRIA_JAR cookie data from string to object. */
    data = JSON.parse(decodeURIComponent(data));
    /**
     * If key is passed as the parameter then return the cookie data specific to the
     * key or else return all the flags present in the WRIA_JAR cookie.
     */

    return key ? data[key] : data;
  }

  return {};
}
/**
 * Sets the data in the shared cookie.
 *
 * @param {String} key Key to be set as the shared data object.
 * @param {String} value Value to be set as the shared data object.
 * @param {String} domain Domain of the cookie.
 * @param {String} cookiePrefix cookiePrefix that exists in WRIA_JAR cookie.
 * Applicable keys are `app` and `ceo`.
 */


function setCookieData(cookiePrefix, key, value, domain) {
  /** Get the existing cookie value. */
  const cookieData = getCookieData(cookiePrefix);
  /**
   * Merge the data stored in the WRIA_JAR cookie with the data to be stored in
   * the WRIA_JAR cookie. The data to be stored in the WRIA_JAR cookie is passed
   * to the setCookie method via `key` and `value` parameters.
   */

  const data = _objectSpread(_objectSpread({}, cookieData), {}, {
    [key]: value
  });

  const currentWRIAJARCookieValue = document.cookie.match(getWriaJarCookieRegex(cookiePrefix));

  if (currentWRIAJARCookieValue) {
    /** Delete the existing cookie */
    document.cookie = `${WRIA_COOKIE_NAME}=;path=/;domain=${domain};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  const dataString = JSON.stringify(data);
  /**
   * Create the WRIA_JAR cookie with the current key and value
   * passed in via parameters
   */

  document.cookie = `${WRIA_COOKIE_NAME}=${cookiePrefix}:${encodeURIComponent(dataString)};path=/;domain=${domain};secure`;
  return data;
}

export default {
  setCookieData,
  getCookieData
};